import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { authService } from '../services/authService';
import { useAuthStore } from '../../../stores/authStore';
import type { RegisterRequest } from '../../../types';

const registerSchema = z.object({
  username: z.string().max(50, '用户名最长50字符').optional().or(z.literal('')),
  password: z.string().min(6, '密码至少6位'),
  email: z.string().min(1, '请输入邮箱').email('邮箱格式不正确'),
  code: z.string().length(6, '请输入6位验证码'),
  teamName: z.string().max(100, '团队名称最长100字符').optional(),
  inviteCode: z.string().length(8, '邀请码必须是8位').optional().or(z.literal(''))
});

type TeamOption = 'create' | 'join';

export const RegisterForm: React.FC = () => {
  const [teamOption, setTeamOption] = useState<TeamOption>('create');
  const { register: registerUser, isLoading, error } = useAuthStore();
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeMsg, setCodeMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    trigger
  } = useForm<RegisterRequest & { teamName?: string }>({
    resolver: zodResolver(registerSchema)
  });

  const sendCode = async () => {
    if (!(await trigger('email'))) return;
    setSending(true);
    setCodeMsg(null);
    try {
      await authService.sendEmailCode(getValues('email'));
      setCooldown(60);
      setCodeMsg({ ok: true, text: '验证码已发送，请查收邮件（10 分钟内有效）' });
    } catch (e: any) {
      setCodeMsg({ ok: false, text: e?.response?.data?.message || '发送失败，请重试' });
    } finally {
      setSending(false);
    }
  };

  const onSubmit = async (data: RegisterRequest & { teamName?: string }) => {
    try {
      const submitData: RegisterRequest = {
        username: data.username || undefined,
        password: data.password,
        email: data.email,
        code: data.code,
      };

      if (teamOption === 'create' && data.teamName) {
        submitData.teamName = data.teamName;
      } else if (teamOption === 'join' && data.inviteCode) {
        submitData.inviteCode = data.inviteCode.toUpperCase();
      }

      await registerUser(submitData);
    } catch (error) {
      // 错误已经在store中处理
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="邮箱"
        type="email"
        placeholder="用来登录和接收验证码"
        error={errors.email?.message}
        {...register('email')}
      />

      <div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="验证码"
              inputMode="numeric"
              maxLength={6}
              placeholder="6位数字"
              error={errors.code?.message}
              {...register('code')}
            />
          </div>
          <Button type="button" variant="secondary" loading={sending} disabled={cooldown > 0} onClick={sendCode} className="shrink-0 mb-px">
            {cooldown > 0 ? `${cooldown} 秒后重发` : '发送验证码'}
          </Button>
        </div>
        {codeMsg && <p className={`mt-1 text-xs ${codeMsg.ok ? 'text-green-600' : 'text-red-600'}`}>{codeMsg.text}</p>}
      </div>

      <Input
        label="密码"
        type="password"
        placeholder="至少6位密码"
        error={errors.password?.message}
        {...register('password')}
      />

      <Input
        label="用户名 (可选)"
        placeholder="不填就用邮箱 @ 前面的部分"
        error={errors.username?.message}
        {...register('username')}
      />
      
      {/* 团队选项 */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex mb-3">
          <label className="flex items-center mr-6">
            <input
              type="radio"
              name="team-option"
              value="create"
              checked={teamOption === 'create'}
              onChange={(e) => setTeamOption(e.target.value as TeamOption)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">创建团队</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="team-option"
              value="join"
              checked={teamOption === 'join'}
              onChange={(e) => setTeamOption(e.target.value as TeamOption)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">加入团队</span>
          </label>
        </div>
        
        {teamOption === 'create' && (
          <Input
            label="团队名称"
            placeholder="请输入团队名称"
            error={errors.teamName?.message}
            {...register('teamName')}
          />
        )}
        
        {teamOption === 'join' && (
          <Input
            label="邀请码"
            placeholder="请输入8位邀请码"
            maxLength={8}
            style={{ textTransform: 'uppercase' }}
            error={errors.inviteCode?.message}
            {...register('inviteCode')}
          />
        )}
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      
      <Button
        type="submit"
        loading={isLoading}
        className="w-full"
        variant="success"
      >
        注册
      </Button>
    </form>
  );
};